import { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { useLang } from "@/lib/i18n";
import { useAuth, formatApiError } from "@/lib/auth";
import { useTrackModuleView } from "@/lib/activityTracker";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Lock, MessageCircle, Phone, Sparkles, CreditCard } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ExpertConsult() {
  const { t } = useLang();
  const { user } = useAuth();
  const [quota, setQuota] = useState(null);
  const [modalType, setModalType] = useState(null); // 'whatsapp' | 'call' | null
  const [form, setForm] = useState({ subject: "", message: "" });
  const [submitting, setSubmitting] = useState(false);

  useTrackModuleView("expert");

  const fetchQuota = async () => {
    try {
      const { data } = await axios.get(`${API}/expert/quota`, { withCredentials: true });
      setQuota(data);
    } catch (e) {
      setQuota({ eligible: false, reason: "error" });
    }
  };

  useEffect(() => {
    if (user?.role === "client") fetchQuota();
  }, [user]);

  if (!user || user.role !== "client" || !quota) return null;

  const isLocked = !quota.eligible;
  const lockedReason = quota.reason;

  const submit = async () => {
    setSubmitting(true);
    try {
      await axios.post(
        `${API}/expert/request`,
        { type: modalType, subject: form.subject, message: form.message },
        { withCredentials: true }
      );
      toast.success(t.app.expert.success_title, { description: t.app.expert.success_desc });
      setModalType(null);
      setForm({ subject: "", message: "" });
      fetchQuota();
    } catch (e) {
      toast.error("Erreur", { description: formatApiError(e?.response?.data?.detail) });
    } finally {
      setSubmitting(false);
    }
  };

  const remaining = (kind) =>
    quota[kind] ? quota[kind].limit - quota[kind].used : 0;

  return (
    <>
      <section
        data-testid="expert-consult-section"
        className="mt-10 rounded-3xl border border-[#E2E4DF] bg-white p-6 md:p-8"
      >
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-2xl bg-[#E05A3D] grid place-items-center shrink-0">
            <Sparkles size={22} className="text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <h2 className="font-display text-2xl font-bold text-[#1C3A27]">
                {t.app.expert.title}
              </h2>
              {isLocked && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 text-[10px] font-bold uppercase tracking-wider">
                  <Lock size={10} /> Pro
                </span>
              )}
            </div>
            <p className="mt-1 text-sm text-[#5C6B5C]">{t.app.expert.subtitle}</p>
          </div>
        </div>

        {/* Always visible legal disclaimer */}
        <div
          data-testid="expert-section-disclaimer"
          className="mt-4 rounded-xl border border-amber-300 bg-amber-50 p-3 text-xs text-amber-900 leading-relaxed"
        >
          {t.app.expert.legal_disclaimer}
        </div>

        {isLocked ? (
          <div
            data-testid="expert-locked-card"
            className="mt-6 rounded-2xl border-2 border-dashed border-[#E05A3D]/40 bg-[#FFF5F1] p-6"
          >
            <h3 className="font-display text-lg font-bold text-[#1C3A27]">
              {t.app.expert.locked_title}
            </h3>
            <p className="mt-2 text-sm text-[#5C6B5C]">
              {lockedReason === "trial"
                ? t.app.expert.locked_desc_trial
                : t.app.expert.locked_desc_plan}
            </p>
            <Link to="/pay" className="inline-block mt-4">
              <Button
                data-testid="expert-upgrade-btn"
                className="bg-[#1C3A27] hover:bg-[#142A1C] text-white rounded-xl"
              >
                <CreditCard size={16} className="me-2" />
                {t.app.expert.upgrade_btn}
              </Button>
            </Link>
          </div>
        ) : (
          <div className="mt-6 grid sm:grid-cols-2 gap-4">
            {/* WhatsApp question card */}
            <button
              data-testid="expert-whatsapp-btn"
              onClick={() => setModalType("whatsapp")}
              disabled={remaining("whatsapp") <= 0}
              className="text-start rounded-2xl border border-[#E2E4DF] bg-[#FAFAF7] p-5 hover:border-[#25D366] hover:bg-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <div className="flex items-center gap-3">
                <div className="w-11 h-11 rounded-xl bg-[#25D366] grid place-items-center">
                  <MessageCircle size={20} className="text-white" />
                </div>
                <div className="min-w-0">
                  <div className="font-semibold text-[#1C3A27]">
                    {t.app.expert.whatsapp_card}
                  </div>
                  <div className="text-xs text-[#5C6B5C]">
                    {t.app.expert.whatsapp_desc}
                  </div>
                </div>
              </div>
              <div className="mt-3 text-xs font-semibold text-[#E05A3D]">
                {remaining("whatsapp") > 0
                  ? t.app.expert.remaining
                      .replace("{n}", remaining("whatsapp"))
                      .replace("{max}", quota.whatsapp.limit)
                  : t.app.expert.quota_exhausted}
              </div>
            </button>

            {/* Callback card */}
            <button
              data-testid="expert-call-btn"
              onClick={() => setModalType("call")}
              disabled={remaining("call") <= 0}
              className="text-start rounded-2xl border border-[#E2E4DF] bg-[#FAFAF7] p-5 hover:border-[#1C3A27] hover:bg-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <div className="flex items-center gap-3">
                <div className="w-11 h-11 rounded-xl bg-[#1C3A27] grid place-items-center">
                  <Phone size={20} className="text-white" />
                </div>
                <div className="min-w-0">
                  <div className="font-semibold text-[#1C3A27]">
                    {t.app.expert.call_card}
                  </div>
                  <div className="text-xs text-[#5C6B5C]">
                    {t.app.expert.call_desc}
                  </div>
                </div>
              </div>
              <div className="mt-3 text-xs font-semibold text-[#E05A3D]">
                {remaining("call") > 0
                  ? t.app.expert.remaining
                      .replace("{n}", remaining("call"))
                      .replace("{max}", quota.call.limit)
                  : t.app.expert.quota_exhausted}
              </div>
            </button>
          </div>
        )}
      </section>

      <Dialog open={!!modalType} onOpenChange={(o) => !o && setModalType(null)}>
        <DialogContent data-testid="expert-modal" className="max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {modalType === "whatsapp"
                ? t.app.expert.modal_title_whatsapp
                : t.app.expert.modal_title_call}
            </DialogTitle>
            <DialogDescription>
              Seifeddine Ben Rejeb — Ingénieur agronome (conduite d'élevage & nutrition)
            </DialogDescription>
          </DialogHeader>

          {/* Legal disclaimer — visible BEFORE the client submits */}
          <div
            data-testid="expert-legal-disclaimer"
            className="rounded-xl border border-amber-300 bg-amber-50 p-3 text-xs text-amber-900 leading-relaxed"
          >
            {t.app.expert.legal_disclaimer}
          </div>

          <div className="space-y-4">
            <div>
              <label className="text-sm font-semibold text-[#1C3A27]">
                {t.app.expert.subject_label}
              </label>
              <Input
                data-testid="expert-subject-input"
                value={form.subject}
                onChange={(e) => setForm({ ...form, subject: e.target.value })}
                placeholder={t.app.expert.subject_ph}
                maxLength={200}
                className="mt-1.5"
              />
            </div>
            <div>
              <label className="text-sm font-semibold text-[#1C3A27]">
                {t.app.expert.message_label}
              </label>
              <Textarea
                data-testid="expert-message-input"
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                placeholder={t.app.expert.message_ph}
                maxLength={2000}
                rows={5}
                className="mt-1.5"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setModalType(null)}>
              Annuler
            </Button>
            <Button
              data-testid="expert-submit-btn"
              onClick={submit}
              disabled={submitting || !form.message.trim()}
              className="bg-[#1C3A27] hover:bg-[#142A1C] text-white"
            >
              {submitting ? t.app.expert.submitting : t.app.expert.submit}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
